/**
 * 项目热点追踪器
 *
 * 功能：
 * 1. 记录用户常打开的项目
 * 2. 提供热点项目列表（用于空闲预热）
 * 3. 持久化到 localStorage
 *
 * 使用示例：
 *   projectHotspotTracker.recordOpen(crewId, crewName);
 *   const hotProjects = projectHotspotTracker.getHotProjects(5);
 */

class ProjectHotspotTracker {
  constructor() {
    this.storageKey = 'cineart_project_hotspots';
    this.maxRecords = 50; // 最多保留 50 条记录
    this.hotspots = this._load();
  }

  /**
   * 记录项目打开事件
   * @param {string} crewId - 项目 ID
   * @param {string} crewName - 项目名称
   */
  recordOpen(crewId, crewName) {
    if (!crewId) return;

    const now = Date.now();
    const key = String(crewId);

    // 更新或新增记录
    const existing = this.hotspots.find(h => h.crewId === key);
    if (existing) {
      existing.openCount++;
      existing.lastOpenAt = now;
      existing.crewName = crewName; // 更新名称（可能改过）
    } else {
      this.hotspots.push({
        crewId: key,
        crewName,
        openCount: 1,
        lastOpenAt: now,
        firstOpenAt: now,
      });
    }

    // 限制记录数量（保留最近打开的）
    if (this.hotspots.length > this.maxRecords) {
      this.hotspots.sort((a, b) => b.lastOpenAt - a.lastOpenAt);
      this.hotspots = this.hotspots.slice(0, this.maxRecords);
    }

    this._save();
  }

  /**
   * 获取热点项目列表
   * @param {number} limit - 返回数量
   * @returns {Array} - [{crewId, crewName, openCount, lastOpenAt, score}, ...]
   */
  getHotProjects(limit = 5) {
    const now = Date.now();
    const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

    // 计算热度分数：openCount × 时间衰减系数
    const scored = this.hotspots.map(h => {
      const daysSinceOpen = (now - h.lastOpenAt) / (24 * 60 * 60 * 1000);

      // 时间衰减：1 周内全权重，之后指数衰减
      const timeFactor = daysSinceOpen <= 7
        ? 1
        : Math.exp(-(daysSinceOpen - 7) / 14); // 14 天半衰期

      const score = h.openCount * timeFactor;

      return { ...h, score };
    });

    // 按分数排序，取前 N 个
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, limit);
  }

  /**
   * 获取指定项目的热度信息
   * @param {string} crewId
   * @returns {Object|null}
   */
  getHotspot(crewId) {
    return this.hotspots.find(h => h.crewId === String(crewId)) || null;
  }

  /**
   * 清除指定项目的记录（项目被删除时调用）
   * @param {string} crewId
   */
  remove(crewId) {
    const key = String(crewId);
    this.hotspots = this.hotspots.filter(h => h.crewId !== key);
    this._save();
  }

  /**
   * 清空所有记录
   */
  clear() {
    this.hotspots = [];
    this._save();
  }

  /**
   * 从 localStorage 加载
   * @private
   */
  _load() {
    try {
      const json = localStorage.getItem(this.storageKey);
      if (!json) return [];

      const data = JSON.parse(json);
      if (!Array.isArray(data)) return [];

      // 过滤掉 3 个月以上未打开的项目
      const THREE_MONTHS_MS = 90 * 24 * 60 * 60 * 1000;
      const now = Date.now();
      return data.filter(h => (now - h.lastOpenAt) < THREE_MONTHS_MS);
    } catch (err) {
      console.warn('热点追踪数据加载失败:', err);
      return [];
    }
  }

  /**
   * 保存到 localStorage
   * @private
   */
  _save() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.hotspots));
    } catch (err) {
      console.warn('热点追踪数据保存失败（不影响使用）:', err);
    }
  }

  /**
   * 获取统计信息（调试用）
   * @returns {Object}
   */
  getStats() {
    const now = Date.now();
    const recentCount = this.hotspots.filter(h => (now - h.lastOpenAt) < 7 * 24 * 60 * 60 * 1000).length;

    return {
      totalProjects: this.hotspots.length,
      recentProjects: recentCount,
      topProject: this.hotspots.length > 0
        ? this.getHotProjects(1)[0]
        : null,
    };
  }
}

// 导出单例
const projectHotspotTracker = new ProjectHotspotTracker();
export default projectHotspotTracker;
